// design/OpsOverview.dc.html. No dedicated stats endpoint exists (FR-AD7 doesn't ask for one
// beyond "view storage and transcode cost per org," which is per-org, not fleet-wide) — this
// page derives fleet totals from the same GET /ops/orgs and GET /ops/jobs the Orgs and Jobs
// pages already call, so there's one source of truth for each number, not a third endpoint to
// keep in sync with the other two.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import type { ListResponse, OpsJobOut, OpsOrgOut } from "../api/types";
import { formatBytes } from "../library/format";
import { Notice } from "../components/ui";
import OpsTopUsage from "./OpsTopUsage";
import { Stat } from "../components/Stat";

export default function OpsOverviewPage() {
  const [orgs, setOrgs] = useState<OpsOrgOut[] | null>(null);
  const [failedJobs, setFailedJobs] = useState<OpsJobOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api<ListResponse<OpsOrgOut>>("GET", "/ops/orgs"),
      api<ListResponse<OpsJobOut>>("GET", "/ops/jobs?status=failed,dead"),
    ])
      .then(([orgsRes, jobsRes]) => {
        setOrgs(orgsRes.items);
        setFailedJobs(jobsRes.items);
      })
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load the overview."));
  }, []);

  const totalStorage = orgs?.reduce((sum, o) => sum + o.storage_bytes, 0) ?? 0;
  const suspendedCount = orgs?.filter((o) => o.suspended_at).length ?? 0;

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Overview</h1>
          <p>Fleet-wide totals across every org.</p>
        </div>
      </header>

      {error ? <Notice tone="error">{error}</Notice> : null}

      <div className="admin-stats">
        <Stat label="Orgs" value={orgs === null ? "—" : orgs.length} />
        <Stat label="Suspended" value={orgs === null ? "—" : suspendedCount} />
        <Stat label="Storage (approx.)" value={orgs === null ? "—" : formatBytes(totalStorage)} />
        <Stat label="Failed / dead jobs" value={failedJobs === null ? "—" : failedJobs.length} />
      </div>

      <OpsTopUsage />

      <div style={{ display: "flex", gap: 10 }}>
        <Link to="/ops/orgs" className="gk-btn gk-btn--secondary">
          View orgs
        </Link>
        <Link to="/ops/jobs" className="gk-btn gk-btn--secondary">
          View jobs
        </Link>
        <Link to="/ops/email" className="gk-btn gk-btn--secondary">
          Email / SMTP
        </Link>
      </div>
    </>
  );
}
